import SendIcon from '@mui/icons-material/Send';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import type { TFormQuestionGroup, TSubmitFormAnswer, TSupabaseProfile } from '@onesource/schemas';
import {
  ContentBlockType,
  LessonProgressStatus,
  getQuestionInputType,
  isShortAnswerValueValid,
} from '@onesource/schemas';
import type { TFormQuestion } from '@onesource/services';
import {
  fetchFormSubmission,
  getSession,
  useCompleteBlock,
  useGetAllProfileQuery,
  useGetForm,
  useGetFormSubmission,
  useGetLesson,
  useGetLessons,
  useGetSpace,
  useGetUserFormSubmissions,
  useSaveFormProgress,
  useStartFormSubmission,
  useSubmitForm,
  useUpdateProgress,
} from '@onesource/services';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  MdArrowBack as ArrowBackIcon,
  MdCheckCircle,
  MdChevronLeft,
  MdChevronRight,
  MdFileDownload,
} from 'react-icons/md';

import { FormReception } from '#/features/community-hub/components/form/form-reception';
import { QuestionBlock } from '#/features/community-hub/components/form/question-block';
import { downloadSubmissionPdf } from '#/features/community-hub/components/form/submission-pdf';
import { LessonBreadcrumbs } from '#/features/community-hub/components/lesson-breadcrumbs';

// Tiny string hash → 32-bit int, used to seed the shuffle so the order stays
// stable across refresh/resume of the same submission.
const hashString = (s: string): number => {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  }
  return h;
};

// mulberry32 — small, deterministic PRNG seeded by `hashString(submissionId)`
const seededShuffle = <T,>(input: readonly T[], seed: number): T[] => {
  const result = [...input];
  let s = seed >>> 0;
  for (let i = result.length - 1; i > 0; i--) {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    const rand = ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    const j = Math.floor(rand * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
};

// Resolve a dot-path profile key (e.g. 'address.city', 'bio') to a string value.
const getProfileValue = (profile: TSupabaseProfile | undefined, key: string): string | null => {
  if (!profile || !key) return null;
  const dotIndex = key.indexOf('.');
  if (dotIndex !== -1) {
    const top = key.slice(0, dotIndex);
    const sub = key.slice(dotIndex + 1);
    const nested = profile[top as keyof TSupabaseProfile] as Record<string, unknown> | null | undefined;
    const val = nested?.[sub];
    return typeof val === 'string' && val.trim().length > 0 ? val.trim() : null;
  }
  const val = profile[key as keyof TSupabaseProfile];
  if (typeof val === 'string') return val.trim().length > 0 ? val.trim() : null;
  return null;
};

// A "page" shown to the member is either a standalone (ungrouped) question or
// a whole group of questions rendered together.
type TFormPage =
  | { kind: 'question'; question: TFormQuestion }
  | { kind: 'group'; group: TFormQuestionGroup; questions: TFormQuestion[] };

export const Route = createFileRoute(
  '/_protected/community-hub/spaces/$id/modules/$moduleId/lessons/$lessonId/form/$contentId',
)({
  component: FormContentRoute,
});

function FormContentRoute() {
  const { id: spaceId, moduleId, lessonId, contentId } = Route.useParams();
  const navigate = useNavigate();

  const { data: lesson, isLoading: isLoadingLesson, error: lessonError } = useGetLesson(lessonId);
  const { data: module } = useGetSpace(moduleId);
  const { data: lessons } = useGetLessons(moduleId);
  const findParentLesson = lessons?.find(l => l.id === lesson?.parentId);

  // Top-level space (program) name for the submission PDF header. Under container
  // spaces the module's `parent` is the program; single-lesson spaces use the module itself.
  const spaceName = module?.parent?.name ?? module?.name;

  const formBlock = lesson?.contentBlocks?.find(
    block => block.id === contentId && block.blockType === ContentBlockType.FORM,
  );
  const formId = formBlock?.formId;

  const { data: form, isLoading: isLoadingForm, error: formError } = useGetForm(formId || '');
  const { data: userSubmissions, isLoading: isLoadingSubmissions } = useGetUserFormSubmissions(formId || '');

  const token = getSession();
  const { data: profile } = useGetAllProfileQuery({ token });

  const startSubmission = useStartFormSubmission();
  const submitForm = useSubmitForm();
  const saveProgress = useSaveFormProgress();
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const trackedRef = useRef(false);
  const { mutate: updateProgress } = useUpdateProgress(lessonId);
  const { mutate: completeBlock } = useCompleteBlock();
  useEffect(() => {
    if (!trackedRef.current && formBlock) {
      trackedRef.current = true;
      updateProgress({ status: LessonProgressStatus.IN_PROGRESS, lastBlockId: contentId });
    }
  }, [contentId, formBlock, updateProgress]);

  const [submissionId, setSubmissionId] = useState<string | null>(null);
  const [resumeSubmissionId, setResumeSubmissionId] = useState<string | null>(null);
  const [view, setView] = useState<'reception' | 'taking'>('reception');
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, TSubmitFormAnswer>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);

  const handleDownloadPdf = async () => {
    if (!submissionId || !form) return;
    setIsDownloadingPdf(true);
    try {
      const detail = await fetchFormSubmission(submissionId);
      await downloadSubmissionPdf(detail, form.title, form.questions ?? [], spaceName);
    } catch (error) {
      console.error('Failed to download submission PDF:', error);
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  const questions: TFormQuestion[] = useMemo(() => {
    if (!form?.questions) return [];
    return [...form.questions].sort((a, b) => a.position - b.position);
  }, [form]);

  // Build the ordered list of pages. A grouped question collapses into a single
  // group page (emitted at its first occurrence); ungrouped questions are their
  // own page. When shuffle is on, the *pages* are shuffled so groups stay intact.
  const pages: TFormPage[] = useMemo(() => {
    if (!form) return [];
    const groupById = new Map((form.groups ?? []).map(g => [g.id, g]));
    const result: TFormPage[] = [];
    const seenGroups = new Set<string>();
    for (const question of questions) {
      const group = question.groupId ? groupById.get(question.groupId) : undefined;
      if (group) {
        if (seenGroups.has(group.id)) {
          continue;
        }
        seenGroups.add(group.id);
        result.push({
          kind: 'group',
          group,
          questions: questions.filter(q => q.groupId === group.id),
        });
      } else {
        result.push({ kind: 'question', question });
      }
    }
    if (form.shuffleQuestions && submissionId) {
      return seededShuffle(result, hashString(submissionId));
    }
    return result;
  }, [form, questions, submissionId]);

  const currentPage = pages[currentPageIndex];
  const currentPageQuestions: TFormQuestion[] =
    currentPage?.kind === 'group' ? currentPage.questions : currentPage ? [currentPage.question] : [];

  const { data: resumeSubmissionData, isLoading: isLoadingResumeSubmission } = useGetFormSubmission(
    resumeSubmissionId || '',
  );

  const backToLessonUrl = `/community-hub/spaces/${spaceId}/modules/${moduleId}/lessons/${lessonId}`;

  // Hydrate answers from a resumed submission, then enter taking view
  useEffect(() => {
    if (resumeSubmissionId && resumeSubmissionData && form && questions.length > 0) {
      const loaded: Record<string, TSubmitFormAnswer> = {};
      questions.forEach(q => {
        const existing = resumeSubmissionData.answers.find(a => a.questionId === q.id);
        loaded[q.id] = {
          questionId: q.id,
          selectedOptionIds: existing?.selectedOptionIds ?? [],
          orderingSequence: existing?.orderingSequence ?? [],
          textAnswer: existing?.textAnswer ?? '',
          fileUrls: existing?.fileUrls ?? [],
        };
      });
      setAnswers(loaded);
      setSubmissionId(resumeSubmissionData.id);
      setView('taking');
      setResumeSubmissionId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumeSubmissionId, resumeSubmissionData, form, questions]);

  const handleStart = () => {
    if (!formId) return;
    startSubmission.mutate(formId, {
      onSuccess: submission => {
        const initialAnswers: Record<string, TSubmitFormAnswer> = {};
        questions.forEach(q => {
          initialAnswers[q.id] = {
            questionId: q.id,
            selectedOptionIds: [],
            orderingSequence: [],
            textAnswer: '',
            fileUrls: [],
          };
        });
        // Pre-fill answers for questions mapped to a profile field
        questions.forEach(q => {
          if (!q.profileFieldKey) return;
          if (q.questionType !== 'short_answer' && q.questionType !== 'dropdown') return;
          const value = getProfileValue(profile, q.profileFieldKey);
          if (value !== null) {
            initialAnswers[q.id].textAnswer = value;
          }
        });
        setAnswers(initialAnswers);
        setSubmissionId(submission.id);
        setCurrentPageIndex(0);
        setView('taking');
      },
      onError: error => {
        console.error('Failed to start submission:', error);
      },
    });
  };

  const handleResume = (id: string) => {
    setCurrentPageIndex(0);
    setResumeSubmissionId(id);
  };

  const handleAnswerChange = useCallback((answer: TSubmitFormAnswer) => {
    setAnswers(prev => ({
      ...prev,
      [answer.questionId]: answer,
    }));
  }, []);

  // Debounced auto-save while taking the form so progress survives reload/resume.
  useEffect(() => {
    if (view !== 'taking' || !submissionId || isSubmitting || submitted) {
      return;
    }
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      const filled = Object.values(answers).filter(
        a =>
          (a.selectedOptionIds?.length || 0) > 0 ||
          (a.orderingSequence?.length || 0) > 0 ||
          (a.textAnswer?.trim().length || 0) > 0 ||
          (a.fileUrls?.length || 0) > 0,
      );
      saveProgress.mutate({ submissionId, data: { answers: filled } });
    }, 800);
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers, view, submissionId, isSubmitting, submitted]);

  // Scroll to top whenever the page changes. This must run *after* React commits
  // the new page — firing window.scrollTo synchronously inside the click handler
  // lets the subsequent re-render change the document height and abort the
  // in-flight smooth-scroll animation, so it appears to do nothing.
  useEffect(() => {
    if (view !== 'taking') return;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPageIndex, view]);

  const goToPrevious = useCallback(() => {
    setCurrentPageIndex(prev => Math.max(0, prev - 1));
  }, []);
  const goToNext = useCallback(() => {
    setCurrentPageIndex(prev => Math.min(pages.length - 1, prev + 1));
  }, [pages.length]);

  const isQuestionAnswered = (questionId: string): boolean => {
    const answer = answers[questionId];
    if (!answer) {
      return false;
    }
    const question = questions.find(q => q.id === questionId);
    if (!question) {
      return false;
    }
    switch (question.questionType) {
      case 'multiple_choice':
      case 'multiple_select':
      case 'true_false': {
        return (answer.selectedOptionIds?.length || 0) > 0;
      }
      case 'short_answer': {
        return (answer.textAnswer?.trim().length || 0) > 0;
      }
      case 'ordering': {
        return (answer.orderingSequence?.length || 0) > 0;
      }
      case 'file_upload': {
        return (answer.fileUrls?.length || 0) > 0;
      }
      case 'accept': {
        return answer.textAnswer === 'accepted';
      }
      case 'accept_with_signature': {
        try {
          const parsed = JSON.parse(answer.textAnswer || '{}') as { checked?: boolean; name?: string };
          return parsed.checked === true && (parsed.name?.trim().split(/\s+/).filter(Boolean).length ?? 0) >= 2;
        } catch {
          return false;
        }
      }
      case 'dropdown': {
        return (answer.textAnswer?.trim().length || 0) > 0;
      }
      default: {
        return false;
      }
    }
  };

  const answeredCount = questions.filter(q => isQuestionAnswered(q.id)).length;
  const unansweredRequired = questions.filter(q => q.isRequired && !isQuestionAnswered(q.id));
  const hasUnansweredRequired = unansweredRequired.length > 0;

  // Required questions on the *current page* that are still unanswered. While
  // this is non-empty the member cannot advance to the next page.
  const currentPageUnansweredRequired = currentPageQuestions.filter(q => q.isRequired && !isQuestionAnswered(q.id));

  // Questions on the current page whose answer is non-empty but fails validation.
  // Also blocks advancing. accept_with_signature is excluded — incomplete state is
  // already covered by currentPageUnansweredRequired; the inline component handles
  // the name-format error so the page-level alert would appear prematurely.
  const currentPageInvalidAnswers = currentPageQuestions.filter(q => {
    if (q.questionType === 'short_answer') {
      const text = answers[q.id]?.textAnswer ?? '';
      return text.trim().length > 0 && !isShortAnswerValueValid(getQuestionInputType(q.metadata), text);
    }
    return false;
  });

  // True when the current page has a required accept_with_signature that isn't fully complete.
  const hasIncompleteSignature = currentPageQuestions.some(q => {
    if (q.questionType !== 'accept_with_signature' || !q.isRequired) return false;
    return !isQuestionAnswered(q.id);
  });

  const canAdvancePage = currentPageUnansweredRequired.length === 0 && currentPageInvalidAnswers.length === 0;

  const handleJumpToFirstUnansweredRequired = () => {
    if (unansweredRequired.length === 0) {
      return;
    }
    const first = unansweredRequired[0];
    const idx = pages.findIndex(p =>
      p.kind === 'group' ? p.questions.some(q => q.id === first.id) : p.question.id === first.id,
    );
    if (idx >= 0) {
      setCurrentPageIndex(idx);
    }
  };

  const handleSubmit = async () => {
    if (!submissionId) {
      return;
    }
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    setIsSubmitting(true);
    setShowSubmitDialog(false);

    try {
      const answersArray = Object.values(answers).filter(
        a =>
          // Drop empty answers so SubmitFormSchema's min(1) accepts the payload
          // when the user only filled in some questions; backend enforces nothing else.
          (a.selectedOptionIds?.length || 0) > 0 ||
          (a.orderingSequence?.length || 0) > 0 ||
          (a.textAnswer?.trim().length || 0) > 0 ||
          (a.fileUrls?.length || 0) > 0,
      );
      await submitForm.mutateAsync({
        submissionId,
        data: { answers: answersArray },
      });
      // Submitting the form completes this block (backend verifies the submission).
      completeBlock(contentId);
      setSubmitted(true);
    } catch (error) {
      console.error('Failed to submit form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoadingLesson) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (lessonError) {
    return (
      <Box p={3}>
        <Alert severity="error">Failed to load lesson: {lessonError.message}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate({ to: backToLessonUrl })} sx={{ mt: 2 }}>
          Back to Lesson
        </Button>
      </Box>
    );
  }

  if (!formBlock) {
    return (
      <Box p={3}>
        <Alert severity="error">Form content block not found</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate({ to: backToLessonUrl })} sx={{ mt: 2 }}>
          Back to Lesson
        </Button>
      </Box>
    );
  }

  if (!formId) {
    return (
      <Box p={3}>
        <Alert severity="warning">No form associated with this content block</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate({ to: backToLessonUrl })} sx={{ mt: 2 }}>
          Back to Lesson
        </Button>
      </Box>
    );
  }

  if (formBlock.formStatus && formBlock.formStatus !== 'published') {
    return (
      <Box p={3}>
        <Alert severity="info">This form is not available right now.</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate({ to: backToLessonUrl })} sx={{ mt: 2 }}>
          Back to Lesson
        </Button>
      </Box>
    );
  }

  if (isLoadingForm) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (formError) {
    return (
      <Box p={3}>
        <Alert severity="error">Failed to load form: {formError.message}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate({ to: backToLessonUrl })} sx={{ mt: 2 }}>
          Back to Lesson
        </Button>
      </Box>
    );
  }

  if (submitted) {
    return (
      <>
        <LessonBreadcrumbs
          spaceId={spaceId}
          moduleId={moduleId}
          lessonId={lessonId}
          module={module}
          lesson={lesson}
          parentLesson={findParentLesson}
          currentLabel="Form"
        />
        <Box
          sx={{
            minHeight: '60vh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 2,
            px: 3,
          }}
        >
          <MdCheckCircle size={72} color="#4caf50" />
          <Typography variant="h4" sx={{ fontFamily: 'Georgia, serif' }}>
            Thanks for your response!
          </Typography>
          <Typography color="text.secondary" sx={{ textAlign: 'center', maxWidth: 480 }}>
            Your submission for &quot;{form?.title}&quot; has been recorded.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
            <Button
              startIcon={
                isDownloadingPdf ? <CircularProgress size={16} color="inherit" /> : <MdFileDownload size={18} />
              }
              onClick={handleDownloadPdf}
              disabled={isDownloadingPdf}
              variant="outlined"
              sx={{
                px: 3,
                py: 1,
                borderRadius: 2,
                textTransform: 'none',
                borderColor: '#ff4d6d',
                color: '#ff4d6d',
                '&:hover': { borderColor: '#e63956', bgcolor: 'rgba(255,77,109,0.06)' },
              }}
            >
              Download PDF
            </Button>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate({ to: backToLessonUrl })}
              variant="contained"
              sx={{
                bgcolor: '#ff4d6d',
                color: 'white',
                px: 3,
                py: 1,
                borderRadius: 2,
                textTransform: 'none',
                '&:hover': { bgcolor: '#e63956' },
              }}
            >
              Back to Lesson
            </Button>
          </Box>
        </Box>
      </>
    );
  }

  if (view === 'reception' || !submissionId) {
    return (
      <>
        <LessonBreadcrumbs
          spaceId={spaceId}
          moduleId={moduleId}
          lessonId={lessonId}
          module={module}
          lesson={lesson}
          parentLesson={findParentLesson}
          currentLabel="Form"
        />
        {form && (
          <FormReception
            form={form}
            formBlock={formBlock}
            userSubmissions={userSubmissions}
            isLoadingSubmissions={isLoadingSubmissions}
            onStart={handleStart}
            onResume={handleResume}
            isStarting={startSubmission.isPending}
            isResuming={!!resumeSubmissionId && isLoadingResumeSubmission}
            spaceName={spaceName}
          />
        )}
      </>
    );
  }

  if (pages.length === 0) {
    return (
      <Box p={3}>
        <Alert severity="info">This form has no questions yet.</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate({ to: backToLessonUrl })} sx={{ mt: 2 }}>
          Back to Lesson
        </Button>
      </Box>
    );
  }

  const progressPercent = Math.round((currentPageIndex / pages.length) * 100);

  return (
    <>
      <LessonBreadcrumbs
        spaceId={spaceId}
        moduleId={moduleId}
        lessonId={lessonId}
        module={module}
        lesson={lesson}
        parentLesson={findParentLesson}
        currentLabel="Form"
      />

      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: 'white',
          borderRadius: 4,
          p: 2.5,
        }}
      >
        <Box maxWidth="800px" mx="auto">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: { xs: 2, md: 3 } }}>
            <Typography sx={{ fontWeight: 500, minWidth: 40 }}>{progressPercent}%</Typography>
            <LinearProgress
              variant="determinate"
              value={progressPercent}
              sx={{
                flex: 1,
                height: 8,
                borderRadius: 4,
                bgcolor: 'rgba(0,0,0,0.08)',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 4,
                  bgcolor: '#ff4d6d',
                },
              }}
            />
          </Box>
          {currentPage?.kind === 'group' && (
            <Box sx={{ mb: { xs: 2, md: 4 }, textAlign: 'center' }}>
              <Typography sx={{ fontFamily: 'Georgia, serif', fontWeight: 700, fontSize: '1.35rem', color: '#1a1a1a' }}>
                {currentPage.group.title}
              </Typography>
              {currentPage.group.description && (
                <Typography sx={{ fontSize: '0.95rem', color: 'text.secondary', mt: 0.5, maxWidth: 700, mx: 'auto' }}>
                  {currentPage.group.description}
                </Typography>
              )}
            </Box>
          )}

          {currentPageQuestions.map(question => (
            <QuestionBlock
              key={question.id}
              question={question}
              answer={answers[question.id] || { questionId: question.id }}
              onAnswerChange={handleAnswerChange}
              disabled={isSubmitting}
            />
          ))}

          {hasIncompleteSignature && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Please check the acknowledgement and enter your full name to continue.
            </Alert>
          )}

          {!hasIncompleteSignature && currentPageUnansweredRequired.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {currentPageUnansweredRequired.length === 1
                ? 'Answer the required question on this page to continue.'
                : `Answer the ${currentPageUnansweredRequired.length} required questions on this page to continue.`}
            </Alert>
          )}

          {currentPageInvalidAnswers.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Fix the highlighted answer(s) before continuing.
            </Alert>
          )}

          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Button
              startIcon={<MdChevronLeft size={20} />}
              onClick={goToPrevious}
              disabled={currentPageIndex === 0 || isSubmitting}
              sx={{
                bgcolor: '#ff4d6d',
                color: 'white',
                px: 3,
                py: 1,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                '&:hover': { bgcolor: '#e63956' },
                '&:disabled': { bgcolor: '#ccc', color: '#666' },
              }}
            >
              Previous
            </Button>

            {currentPageIndex < pages.length - 1 ? (
              <Button
                endIcon={<MdChevronRight size={20} />}
                onClick={goToNext}
                disabled={isSubmitting || !canAdvancePage}
                sx={{
                  bgcolor: '#ff4d6d',
                  color: 'white',
                  px: 3,
                  py: 1,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 500,
                  '&:hover': { bgcolor: '#e63956' },
                }}
              >
                Next
              </Button>
            ) : (
              <Button
                endIcon={<SendIcon />}
                onClick={() => {
                  if (hasUnansweredRequired) {
                    handleJumpToFirstUnansweredRequired();
                    return;
                  }
                  setShowSubmitDialog(true);
                }}
                disabled={
                  isSubmitting || answeredCount === 0 || hasUnansweredRequired || currentPageInvalidAnswers.length > 0
                }
                sx={{
                  bgcolor: '#4caf50',
                  color: 'white',
                  px: 3,
                  py: 1,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 500,
                  '&:hover': { bgcolor: '#388e3c' },
                  '&:disabled': { bgcolor: '#ccc', color: '#666' },
                }}
              >
                Submit
              </Button>
            )}
          </Box>
        </Box>

        <Dialog open={showSubmitDialog} onClose={() => setShowSubmitDialog(false)}>
          <DialogTitle>Submit Form?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              You have answered {answeredCount} of {questions.length} questions.{' '}
              {answeredCount < questions.length && (
                <Box component="span" sx={{ color: 'warning.main', display: 'block', mt: 1 }}>
                  Note: {questions.length - answeredCount} question(s) will be left blank.
                </Box>
              )}
              Are you sure you want to submit your response?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" onClick={() => setShowSubmitDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} variant="contained" color="primary">
              Submit
            </Button>
          </DialogActions>
        </Dialog>

        {isSubmitting && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 9999,
            }}
          >
            <CircularProgress size={60} />
            <Typography variant="h6" sx={{ mt: 2 }}>
              Submitting your response...
            </Typography>
          </Box>
        )}
      </Box>
    </>
  );
}
